import type { BadgeProps } from '@aidkitorg/component-library';
import type { SupportedColor } from './Color';
import { normalizeTwilioStatus } from '@aidkitorg/types/lib/messaging/deliveryStatus';
import type { DeliveryStatus as CanonicalDeliveryStatus } from '@aidkitorg/types/lib/messaging/deliveryStatus';

// BadgeVariant isn't re-exported from the component-library barrel.
type BadgeVariant = NonNullable<BadgeProps['variant']>;

/**
 * Presentation for `message.status`
 *
 * Every lookup first normalizes the stored value with the backend's `normalizeTwilioStatus`, so
 * raw provider strings on older rows (`accepted`, `sending`, `canceled`, ...) render as the
 * canonical status the Overview counts them under.
 *
 * `received` is the one non-provider value: it labels inbound messages.
 */
type DisplayStatus = CanonicalDeliveryStatus | 'received';

function toDisplayStatus(status: string | undefined | null): DisplayStatus | null {
  return status === 'received' ? status : normalizeTwilioStatus(status ?? '');
}

/**
 * `read` is WhatsApp-only and must stay distinct from `delivered`, hence indigo.
 * It shares indigo with `received`, which is tolerable: `received`
 * only ever labels inbound messages, and the labels differ.
 */
const COLORS: Record<DisplayStatus, SupportedColor> = {
  queued: 'gray',
  scheduled: 'yellow',
  sent: 'blue',
  delivered: 'green',
  read: 'indigo',
  received: 'indigo',
  undelivered: 'red',
  failed: 'red',
};

/** Bridge to the component-library Badge palette, which the admin panel standardizes on. */
const COLOR_TO_VARIANT: Record<SupportedColor, BadgeVariant> = {
  gray: 'neutral',
  white: 'neutral',
  yellow: 'warning',
  blue: 'info',
  indigo: 'info',
  green: 'success',
  red: 'error',
};

/** Falls back to gray for an unrecognized status, as the map this replaces did. */
export function deliveryStatusColor(status?: string | null): SupportedColor {
  const displayStatus = toDisplayStatus(status);
  return displayStatus ? COLORS[displayStatus] : 'gray';
}

/** For the component-library `Badge`, used by the admin panel tables. */
export function deliveryStatusVariant(status?: string | null): BadgeVariant {
  return COLOR_TO_VARIANT[deliveryStatusColor(status)];
}

/**
 * Resolves a localized label, falling back to the raw value so an unrecognized provider status
 * still shows something. Pass `L.applicant.comms`, which holds a label for every display status.
 */
export function deliveryStatusLabel(
  status: string | undefined | null,
  strings: Record<DisplayStatus, string>,
): string {
  const displayStatus = toDisplayStatus(status);
  return displayStatus ? strings[displayStatus] : status ?? '';
}

/**
 * Every display status plus `not_tracked`, an Overview bucket that no message row holds.
 * Requiring this shape of the translations means a new canonical status won't compile until
 * `en.json` defines it.
 */
export type DeliveryStatusDefinitionKey = DisplayStatus | 'not_tracked';

/**
 * Plain-language explanation of a status, for the tooltip on its badge or column header.
 * Undefined for an unrecognized status, so callers render the badge without a tooltip.
 */
export function deliveryStatusDefinition(
  status: string | undefined | null,
  definitions: Record<DeliveryStatusDefinitionKey, string>,
): string | undefined {
  const key = status === 'not_tracked' ? status : toDisplayStatus(status);
  return key ? definitions[key] : undefined;
}

/** `message.status` is NOT NULL defaulting to '', so empty means "no provider event yet". */
export function hasDeliveryStatus(status?: string | null): status is string {
  return !!status && status.trim() !== '';
}
